// Accessible form field primitives for the admin management forms
// (accessibility.md §2/§7). `<Field>` wires <label>, the control, and an
// error message with role="alert" + aria-describedby. `<TextInput>` and
// `<SelectInput>` are thin styled controls that forward refs (so
// react-hook-form's register works).
//
// PRD-60 chunk 0: tokens instead of palette steps; 16px text below `md` so
// iOS never zooms into a field; the placeholder clears 4.5:1 (it was
// slate-400, 2.6:1); the only focus treatment is the accent border — the
// global `:focus-visible` outline in index.css does the rest.

import { cn } from "@lib/cn"
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react"
import { useTranslation } from "react-i18next"

export const controlClasses =
  "rounded-control border-border bg-surface text-ink placeholder:text-ink-subtle min-h-11 w-full border px-3 py-2 text-base transition-colors duration-150 md:min-h-9 md:text-sm focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:bg-inset disabled:text-ink-subtle aria-[invalid=true]:border-danger"

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(controlClasses, className)} {...props} />
  ),
)
TextInput.displayName = "TextInput"

export const SelectInput = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, ...props }, ref) => (
    <select ref={ref} className={cn(controlClasses, className)} {...props}>
      {children}
    </select>
  ),
)
SelectInput.displayName = "SelectInput"

interface FieldProps {
  label: string
  /** Renders the control via a render-prop so we can wire ids + aria. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
  error?: string
  hint?: string
  required?: boolean
  className?: string
}

export function Field({ label, children, error, hint, required, className }: FieldProps) {
  const { t } = useTranslation("common")
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const describedBy = error ? errorId : hint ? hintId : undefined

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-ink text-sm font-medium">
        {label}
        {/* The asterisk is for eyes; the words are for a screen reader,
            which never heard that a field was required (PRD-60 chunk B). */}
        {required && (
          <>
            <span className="text-danger-ink ml-0.5" aria-hidden="true">
              *
            </span>
            <span className="sr-only"> ({t("field.required")})</span>
          </>
        )}
      </label>
      {children({ id, describedBy, invalid: !!error })}
      {hint && !error && (
        <p id={hintId} className="text-meta">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-danger-ink text-xs">
          {error}
        </p>
      )}
    </div>
  )
}
